<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useForm } from '@inertiajs/vue3';
import { Modal } from "bootstrap";
import Editor from '@tinymce/tinymce-vue';
import Multiselect from '@vueform/multiselect';
import { useToast } from 'vue-toast-notification';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    card: Boolean,
    roomDetails: Object,
    roomamenities: Array,
    rooms: Object,
    allRoomCategory: Array,
    flash: Object
});

let modalEle = ref(null);
let thisModalObj = null;
let isLoading = ref(false);
const $toast = useToast();

watch(() => props.card, (newValue) => {
    if (newValue && thisModalObj) {
        thisModalObj.show();
    } else if (thisModalObj) {
        thisModalObj.hide();
    }
});

onMounted(() => {
    thisModalObj = new Modal(modalEle.value);
    if (props.card) {
        thisModalObj.show();
    }
});

const value = props.roomDetails?.facilities;
const options = props.roomamenities?.filter(r => !value.find(v => v.id === r.id));

const RoomEditform = useForm({
    description: props.roomDetails?.description,
    baby_cot_availability: props.roomDetails?.baby_cot_availability,
    number_of_rooms: props.roomDetails?.number_of_rooms,
    extra_bed_allowed: props.roomDetails?.extra_bed_allowed,
    room_category_id: props.roomDetails?.room_category?.id,
    amenity_id: props.roomDetails?.facilities.map((item) => {
        return {
            value: item.name,
            name: item.name,
            id: item.id
        }
    }),
    room_id: props.roomDetails?.id,
    room_image: null,
});

const RoomEditformSubmit = () => {
    RoomEditform.post(route('rooms.update'), {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            if (thisModalObj) {
                thisModalObj.hide();
            }
            $toast.open({
                message: props.flash.message,
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
            setTimeout(() => {
                window.location.href = `/hotels/showHotelRooms/${props.roomDetails?.hotel_id}`;
            }, 800);
        },
        onError: (errors) => {
            console.error('Form submission errors:', errors);
            $toast.open({
                message: 'Error updating room details',
                type: "error",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-danger show'
            });
        }
    });
};

function closeRoomEditModal() {
    return window.location.href = '/hotels/showHotelRooms/' + props.roomDetails?.hotel_id;
}

// TinyMCE configuration
const editorConfig = {
    plugins: 'link lists image code table wordcount',
    toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | link image | code',
    height: 300,
    menubar: false,
    branding: false
};
</script>
<style scoped>
@import "@vueform/multiselect/themes/default.css";
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <div class="vld-parent">
        <Loading v-model:active="isLoading" :color="'#696cff'" />
    </div>
    <div class="modal-onboarding modal fade animate__animated" id="onboardHorizontalImageModal" tabindex="-1"
        ref="modalEle" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <form class="pl-3 pr-3" @submit.prevent="RoomEditformSubmit">
                <div class="modal-content">
                    <div class="modal-body onboarding-horizontal p-0">
                        <div class="onboarding-content mb-0">
                            <h4 class="onboarding-title text-body mb-4">Edit Room Details</h4>

                            <div class="row g-3">
                                <div class="col-md-12">
                                    <label for="room_category_id">Room Category</label>
                                    <select class="form-select" name="room_category_id" required="required"
                                        v-model="RoomEditform.room_category_id">
                                        <option value="" disabled="disabled" selected="selected">Select room category
                                            ...</option>
                                        <option v-for="roomCategory in allRoomCategory" :value="roomCategory.id">
                                            {{ roomCategory.name }}</option>
                                    </select>
                                    <div v-if="RoomEditform.errors.room_category_id" class="invalid-feedback">
                                        <div v-text="RoomEditform.errors.room_category_id">
                                        </div>
                                    </div>
                                </div>

                                <div class="row g-3">
                                    <div class="col-md-3">
                                        <label for="baby_cot_availability" class="col-form-label"><b>Baby Cot
                                                Available</b></label>
                                        <select class="form-select" name="baby_cot_availability"
                                            v-model="RoomEditform.baby_cot_availability">
                                            <option disabled="disabled" selected="selected">Select option ...</option>
                                            <option v-if="RoomEditform.baby_cot_availability == '0'" selected value="0">
                                                No
                                            </option>
                                            <option v-if="RoomEditform.baby_cot_availability == '1'" selected value="1">
                                                Yes </option>
                                            <option v-if="!RoomEditform.baby_cot_availability == '0'" value="0"> No
                                            </option>
                                            <option v-if="!RoomEditform.baby_cot_availability == '1'" value="1"> Yes
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <label for="extra_bed_allowed" class="col-form-label"><b>Extra Bed
                                                Allowed</b></label>
                                        <select class="form-select" name="extra_bed_allowed"
                                            v-model="RoomEditform.extra_bed_allowed">
                                            <option disabled="disabled" selected="selected">Select option ...</option>
                                            <option v-if="RoomEditform.extra_bed_allowed == '0'" selected value="0">
                                                No
                                            </option>
                                            <option v-if="RoomEditform.extra_bed_allowed == '1'" selected value="1">
                                                Yes </option>
                                            <option v-if="!RoomEditform.extra_bed_allowed == '0'" value="0"> No
                                            </option>
                                            <option v-if="!RoomEditform.extra_bed_allowed == '1'" value="1"> Yes
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <label for="number_of_rooms" class="col-form-label"><b>No.of Units</b></label>
                                        <input type="number" class="form-control" name="number_of_rooms"
                                            placeholder="Number of units of this type" required="required"
                                            v-model="RoomEditform.number_of_rooms">
                                        <div v-if="RoomEditform.errors.number_of_rooms" class="invalid-feedback">
                                            <div v-text="RoomEditform.errors.number_of_rooms">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <label for="room_image" class="col-form-label"><b>Room Image </b><span
                                                class="text-danger">(Unchanged
                                                until a
                                                new
                                                file is uploaded.)</span></label>
                                        <input type="file" name="room_image" class="form-control"
                                            @input="RoomEditform.room_image = $event.target.files[0]">
                                        <progress v-if="RoomEditform.progress" :value="RoomEditform.progress.percentage"
                                            max="100">
                                            {{ RoomEditform.progress.percentage }}%
                                        </progress>
                                    </div>
                                </div>
                                <div class="row g-3 mb-3">
                                    <label for="room_category_id"><b>Room Amenities</b></label>
                                    <Multiselect v-model="RoomEditform.amenity_id" :options="options" mode="tags"
                                        :closeOnSelect="false" :searchable=true placeholder="Select room amenities ..."
                                        track-by="name" label="name" :object="true" :required=true />
                                </div>
                                <div class="row g-1">
                                    <label for="description"><b>Description</b></label>
                                    <Editor v-model="RoomEditform.description" :init="editorConfig"
                                        api-key="wox9oeifybg6hnw4p4ynmnjisdbotkujgb5pern6xigacveg" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 mt-3">
                        <button type="button" class="btn btn-label-secondary" @click="closeRoomEditModal">
                            Close
                        </button>
                        <button type="submit" class="btn btn-primary">Save Changes</button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>